import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { mdiBankOutline, mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";

import Header from "components/Header";
import PlaidLinkComponent from "components/PlaidLink";
import { routes } from "constants/routes";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { IPlaidAccount } from "models/HaulPay";
import { linkPlaidAccountAPI } from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import { getFactoringProfile } from "store/user/actions";
import styles from "./style.module.scss";

export default function BankAccountSettings() {
  const dispatch = useDispatch<AppDispatch>();
  const { factoringProfile, userDetails } = useSelector((state: RootState) => state.userState);
  const [isUpdating, setIsUpdating] = useState(false);

  const bankAccounts = useMemo<Partial<IPlaidAccount>[]>(() => {
    return factoringProfile?.accounts || [];
  }, [factoringProfile]);

  useEffect(() => {
    if (userDetails?.factoring_id) {
      dispatch(getFactoringProfile(userDetails.factoring_id));
    }
  }, [dispatch, userDetails?.factoring_id]);

  const handlePlaidSuccess = async (publicToken: string, accounts: any[]) => {
    setIsUpdating(true);
    dispatch(handleLoading(true));

    try {
      const requestBody = {
        public_token: publicToken,
        account_ids: accounts.map((account) => account.id)
      };

      await linkPlaidAccountAPI(requestBody);
      showSuccessMessage("Bank account updated successfully!");

      if (userDetails?.factoring_id) {
        await dispatch(getFactoringProfile(userDetails.factoring_id));
      }
    } catch (error: any) {
      showErrorMessage(error?.message || "Failed to update bank account. Please try again.");
    } finally {
      setIsUpdating(false);
      dispatch(handleLoading(false));
    }
  };

  const handlePlaidFailed = () => {
    showErrorMessage("Bank account linking was cancelled or failed.");
  };

  const formatAccountNumber = (accountNumber: string | undefined) => {
    if (!accountNumber) return "****";
    return `****${accountNumber.slice(-4)}`;
  };

  return (
    <MainLayout
      desktopTitle="Bank Account"
      customHeader={
        <Header
          label="Bank Account"
          headerIcon={<Icon path={mdiInformationOutline} />}
          goBackPath={routes.SETTINGS}
          isHideDesktop
        />
      }
      hideNavigation
      className={styles.bankAccountContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.sectionHeader}>
          <Icon path={mdiBankOutline} className="size-6 text-primary" />
          <h2>Your Bank Accounts</h2>
        </div>

        {bankAccounts.length > 0 ? (
          <div className={styles.accountsList}>
            {bankAccounts.map((account, index) => (
              <div key={account.id || index} className={styles.accountCard}>
                <div className={styles.accountInfo}>
                  <div className={styles.bankName}>
                    <Icon path={mdiBankOutline} className="size-5 text-subtitle" />
                    <span>{account.bank_name || "Bank Account"}</span>
                  </div>
                  <div className={styles.accountDetails}>
                    <span className={styles.accountType}>
                      {account.type || account.ach_type || "Checking"}
                    </span>
                    <span className={styles.accountNumber}>
                      {formatAccountNumber(account.account_number_redacted)}
                    </span>
                    {account.routing_number && (
                      <span className={styles.routingNumber}>
                        Routing: ****{account.routing_number.slice(-4)}
                      </span>
                    )}
                  </div>
                  {account.default && <span className={styles.defaultBadge}>Default</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Icon path={mdiBankOutline} className="size-12 text-subtitle" />
            <p>No bank accounts linked yet.</p>
            <p className="text-sm text-subtitle">
              Link your bank account to receive payments faster.
            </p>
          </div>
        )}

        <div className={styles.updateSection}>
          <div className={styles.updateInfo}>
            <h3>{bankAccounts.length > 0 ? "Update Bank Account" : "Add Bank Account"}</h3>
            <p>
              {bankAccounts.length > 0
                ? "Link a new bank account to update your payment information. Your previous account will be replaced."
                : "Link your bank account securely using Plaid to receive payments."}
            </p>
          </div>

          <PlaidLinkComponent
            label={bankAccounts.length > 0 ? "Update Bank Account" : "Add Bank Account"}
            btnClass="!h-12 !bg-primary hover:!bg-primary/90"
            className="w-full desktop:w-fit"
            onPlaidSuccess={handlePlaidSuccess}
            onPlaidFailed={handlePlaidFailed}
          />

          {isUpdating && <p className={styles.updatingText}>Updating your bank account...</p>}
        </div>

        <div className={styles.securityNote}>
          <Icon path={mdiInformationOutline} className="size-5 text-info flex-shrink-0" />
          <p>
            Your bank information is securely processed through Plaid. We never store your login
            credentials.
          </p>
        </div>
      </div>
    </MainLayout>
  );
}
